/**
 * The chapters, and the regions the map can jump to.
 *
 * The era table is the owner's, with the automobile-to-interstate middle
 * split into three: before the Thruway, the Thruway without the Northway, and
 * after the Northway. That split is where the memories this mode is built for
 * actually live.
 */

import type { Era, EraId, Region, RegionId } from './types';

export const ERAS: readonly Era[] = [
  {
    id: 'sloop',
    name: 'Sloop and stagecoach',
    years: [1700, 1825],
    travel: 'On foot, on horseback, by stagecoach, or by sloop on the Hudson',
    nycToBuffalo: 'Two to three weeks',
    blurb:
      'Dirt roads that turned to mud each spring, and a river passage that waited on the tide and the wind. North of Glens Falls was mostly wilderness.',
  },
  {
    id: 'canal',
    name: 'The canal years',
    years: [1825, 1851],
    travel: 'Packet boat on the Erie Canal, towed by horse or mule at four miles an hour',
    nycToBuffalo: 'Six to eight days',
    blurb:
      'The canal cut freight costs to a fraction and made the Mohawk Valley the road west. Passengers ate and slept aboard.',
  },
  {
    id: 'rail',
    name: 'The railroad age',
    years: [1851, 1920],
    travel: 'Steam trains, the New York Central along the Water Level Route',
    nycToBuffalo: 'Eight to twelve hours',
    blurb:
      'A day across the state instead of a week. Towns grew around their depots, and the Adirondacks opened up to anyone with a ticket.',
  },
  {
    id: 'auto',
    name: 'Before the Thruway',
    years: [1920, 1954],
    travel: 'The family car on Route 5, Route 20 and Route 9',
    nycToBuffalo: 'Fourteen to eighteen hours, usually over two days',
    blurb:
      'Two lanes through every downtown, traffic lights, grade crossings and narrow curves. The Hudson had few bridges, so a trip often meant a ferry.',
  },
  {
    id: 'thruway',
    name: 'The Thruway opens',
    years: [1954, 1967],
    travel: 'The New York State Thruway, while the Northway was still being built',
    nycToBuffalo: 'Six to seven hours',
    blurb:
      'Limited access, no stoplights, and towns bypassed overnight. North of Albany you were still on Route 9 through every village.',
  },
  {
    id: 'northway',
    name: 'The Northway years',
    years: [1967, 1985],
    travel: 'The Thruway and the Northway, Interstate 87 all the way to Canada',
    nycToBuffalo: 'Six to seven hours',
    blurb:
      'Albany to Montreal fell from half a day on Route 9 to under four hours. The Adirondacks became a weekend, and Route 9 went quiet.',
  },
  {
    id: 'modern',
    name: 'Today',
    years: [1985, 2100],
    travel: 'Interstates, Amtrak, and a regional flight',
    nycToBuffalo: 'Six to seven hours by car, about eight and a half by train, an hour and a quarter by air',
    blurb:
      'Electronic tolls, a rest area every forty miles, and a whole state that fits inside a day.',
  },
];

const ERA_INDEX: ReadonlyMap<EraId, Era> = new Map(ERAS.map((e) => [e.id, e] as const));

export function eraById(id: EraId): Era {
  const era = ERA_INDEX.get(id);
  if (!era) throw new Error(`backroads: unknown era ${id}`);
  return era;
}

/** The era a year falls in. Years before the first chapter belong to it. */
export function eraForYear(year: number): Era {
  for (const era of ERAS) {
    if (year >= era.years[0] && year < era.years[1]) return era;
  }
  return year < (ERAS[0] as Era).years[0] ? (ERAS[0] as Era) : (ERAS[ERAS.length - 1] as Era);
}

/**
 * Where the map can jump to with one big button. Boxes overlap on purpose:
 * a player who thinks of Lake George as "the North Country" should still
 * find it under that button.
 */
export const REGIONS: readonly Region[] = [
  { id: 'state', name: 'Whole state', bbox: [-79.9, 40.45, -71.75, 45.05] },
  { id: 'adirondacks', name: 'Adirondacks', bbox: [-75.35, 43.15, -73.25, 44.75] },
  { id: 'northcountry', name: 'North Country', bbox: [-76.5, 43.7, -73.3, 45.05] },
  { id: 'champlain', name: 'Champlain Valley', bbox: [-74.0, 43.3, -73.15, 45.05] },
  { id: 'capital', name: 'Capital District', bbox: [-74.4, 42.35, -73.35, 43.35] },
  { id: 'mohawk', name: 'Mohawk Valley', bbox: [-75.7, 42.6, -73.6, 43.4] },
  { id: 'catskills', name: 'Catskills', bbox: [-75.5, 41.55, -73.75, 42.5] },
  { id: 'hudson', name: 'Hudson Valley', bbox: [-74.4, 40.85, -73.35, 42.5] },
  { id: 'fingerlakes', name: 'Finger Lakes', bbox: [-77.8, 42.2, -75.9, 43.4] },
  { id: 'western', name: 'Western New York', bbox: [-79.9, 42.0, -77.3, 43.5] },
  { id: 'southerntier', name: 'Southern Tier', bbox: [-79.9, 41.55, -74.5, 42.55] },
  { id: 'central', name: 'Central New York', bbox: [-76.9, 42.4, -75.0, 43.6] },
  { id: 'across', name: 'Albany to Buffalo', bbox: [-79.1, 42.4, -73.5, 43.35] },
  { id: 'northroad', name: 'Albany to the border', bbox: [-74.3, 42.78, -73.15, 45.05] },
  { id: 'sacandaga', name: 'Sacandaga country', bbox: [-74.65, 42.95, -73.6, 43.65] },
];

const REGION_INDEX: ReadonlyMap<RegionId, Region> = new Map(REGIONS.map((r) => [r.id, r] as const));

export function regionById(id: RegionId): Region {
  const region = REGION_INDEX.get(id);
  if (!region) throw new Error(`backroads: unknown region ${id}`);
  return region;
}
